import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Model-specific timeout limits (in milliseconds)
const getModelTimeout = (modelKey: string): number => {
  if (!modelKey) return 120000; // 2 minutes default
  
  const lowerModel = modelKey.toLowerCase();
  
  // GPT Image models get 3 minutes timeout
  if (lowerModel.includes('gpt-image') || lowerModel.includes('gpt_image')) {
    return 180000; // 3 minutes
  }
  
  // All other models get 2 minutes
  return 120000; // 2 minutes
};

interface GuestGenerationState {
  sessionToken: string | null;
  remainingGenerations: number;
  totalLimit: number;
  isLoading: boolean;
  generatedImages: any[];
}

interface GenerateGuestImageParams {
  prompt: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  numberOfImages: number;
}

export const useGuestImageGeneration = () => {
  const [state, setState] = useState<GuestGenerationState>({
    sessionToken: null,
    remainingGenerations: 5,
    totalLimit: 5,
    isLoading: false,
    generatedImages: []
  });

  // Load guest session from localStorage and fetch persisted images from database
  useEffect(() => {
    const run = async () => {
      const savedSession = localStorage.getItem('guest_image_session');
      if (!savedSession) return;
      try {
        const sessionData = JSON.parse(savedSession);
        const sessionToken = sessionData.token;
        
        // First try to load persisted images from database
        let finalImages: any[] = [];
        try {
          const { data: persistedImages, error } = await supabase
            .rpc('get_guest_images_by_token', { 
              session_token: sessionToken 
            });
          
          if (!error && persistedImages && persistedImages.length > 0) {
            console.log('🎨 Loaded persisted guest images from database:', persistedImages.length);
            // Convert database format to expected format
            finalImages = persistedImages.map((img: any) => ({
              id: img.id,
              prompt: img.prompt,
              image_url: img.image_url,
              model: img.model,
              size: img.size,
              quality: img.quality,
              style: img.style,
              created_at: img.created_at,
              original_settings: img.original_settings
            }));
          }
        } catch (dbError) {
          console.error('Error fetching persisted guest images:', dbError);
        }
        
        // Fallback to localStorage images if no database images found
        if (finalImages.length === 0) {
          const images: any[] = Array.isArray(sessionData.images) ? sessionData.images : [];
          const needsConvert = images.some((img) => typeof img?.image_url === 'string' && img.image_url.startsWith('http'));
          finalImages = images;

          if (needsConvert) {
            try {
              const converted = await Promise.all(images.map(async (img) => {
                const url = img?.image_url;
                if (typeof url === 'string' && url.startsWith('http')) {
                  try {
                    const { data, error } = await supabase.functions.invoke('proxy-image-b64', { body: { url } });
                    if (!error && data?.dataUrl) {
                      return { ...img, image_url: data.dataUrl };
                    }
                  } catch (e) {
                    console.warn('Proxy convert failed for', url, e);
                  }
                  // Fallback: keep original URL for faster display
                  return { ...img, image_url: url }; // was placeholder before
                }
                return img;
              }));
              finalImages = converted.filter(Boolean) as any[];
              // Persist cleaned images back to storage
              const newSession = {
                token: sessionData.token,
                remaining: sessionData.remaining || 3,
                images: finalImages,
                timestamp: Date.now(),
              };
              localStorage.setItem('guest_image_session', JSON.stringify(newSession));
            } catch (e) {
              console.warn('Batch convert failed', e);
            }
          }
        }

        setState(prev => ({
          ...prev,
          sessionToken: sessionToken,
          remainingGenerations: sessionData.remaining || 5,
          generatedImages: finalImages
        }));
      } catch (error) {
        console.error('Error loading guest session:', error);
        localStorage.removeItem('guest_image_session');
      }
    };
    run();
  }, []);

  // Save session to localStorage when it changes (lightweight metadata only)
  const saveSession = (token: string, remaining: number, images: any[]) => {
    try {
      // Only store lightweight metadata, not the full base64 images
      const lightweightImages = images.map(img => ({
        id: img.id,
        prompt: img.prompt?.substring(0, 100) || '', // Truncate prompt
        model: img.model,
        size: img.size,
        created_at: img.created_at,
        // Don't store image_url to save space
        hasImage: !!img.image_url
      }));
      
      const sessionData = {
        token,
        remaining,
        images: lightweightImages,
        timestamp: Date.now()
      };
      
      localStorage.setItem('guest_image_session', JSON.stringify(sessionData));
    } catch (error) {
      console.warn('Failed to save guest session (storage quota exceeded):', error);
      // Still update the in-memory state even if localStorage fails
      setState(prev => ({
        ...prev,
        sessionToken: token,
        remainingGenerations: remaining
      }));
    }
  };

  // Live sync remaining guest generations from Edge Function
  const refreshRemaining = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-guest-limit');
      if (!error && data) {
        const rem = Number(
          (data as any).remainingGenerations ?? (data as any).remaining ?? (data as any).remaining_generations
        );
        const max = Number((data as any).max_limit ?? (data as any).dailyLimit);
        setState(prev => ({
          ...prev,
          remainingGenerations: !Number.isNaN(rem) ? rem : prev.remainingGenerations,
          totalLimit: !Number.isNaN(max) && max > 0 ? max : prev.totalLimit,
        }));
      }
    } catch (e) {
      // ignore
    }
  };

  // Poll on mount and on tab focus to keep UI in sync with admin view
  useEffect(() => {
    refreshRemaining();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshRemaining();
    };
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(refreshRemaining, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(id);
    };
  }, []);

  const generateImage = async (params: GenerateGuestImageParams) => {
    if (state.remainingGenerations <= 0) {
      toast.error('Daily limit reached. Please sign up to continue generating images.');
      return null;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      console.log('🎨 Starting guest image generation with params:', {
        prompt: params.prompt?.substring(0, 50) + '...',
        model: params.model,
        size: params.size
      });

      // Get model-specific timeout
      const modelTimeout = getModelTimeout(params.model);
      console.log(`⏱️ Using ${modelTimeout / 1000}s timeout for guest model: ${params.model}`);

      const { data, error } = await supabase.functions.invoke('generate-guest-images', {
        body: {
          prompt: params.prompt,
          model: params.model,
          size: params.size,
          quality: params.quality,
          style: params.style,
          numberOfImages: Math.min(params.numberOfImages || 1, 1)
        }
      });

      console.log('🎨 Guest generation response:', {
        hasData: !!data,
        hasError: !!error,
        success: data?.success,
        imageCount: data?.images?.length,
        debug: data?.debug
      });

      if (error) {
        console.error('🎨 Supabase function error:', error);
        throw error;
      }

      if (data.error) {
        console.error('🎨 API error response:', data.error);
        if (data.error.includes('limit exceeded')) {
          toast.error(`Daily limit reached! You can generate ${data.max_limit} images per day. Reset: ${data.reset_date}`);
          setState(prev => ({ ...prev, remainingGenerations: 0 }));
        } else {
          toast.error(data.error);
        }
        return null;
      }

      // Normalize images to base64 (CSP-safe)
      const incoming = Array.isArray(data.images) ? data.images : [];
      console.log('🎨 Processing incoming images:', {
        count: incoming.length,
        sampleImageUrl: incoming[0]?.image_url?.substring(0, 50) + '...'
      });

      const processed = await Promise.all(incoming.map(async (img: any) => {
        const url = img?.image_url || img?.url;
        if (typeof url === 'string' && url.startsWith('http')) {
          console.log('🎨 Converting HTTP URL to base64:', url.substring(0, 50) + '...');
          try {
            const { data: pData, error: pErr } = await supabase.functions.invoke('proxy-image-b64', { body: { url } });
            if (!pErr && pData?.dataUrl) {
              console.log('🎨 Successfully converted to base64');
              return { ...img, image_url: pData.dataUrl };
            }
            console.warn('🎨 Proxy conversion failed:', pErr);
          } catch (e) {
            console.warn('🎨 Proxy convert failed for', url, e);
          }
          // Fallback: keep original URL for faster display
          return { ...img, image_url: url };
        }
        if (typeof url === 'string' && (url.startsWith('data:') || url.startsWith('blob:'))) {
          console.log('🎨 Image already in base64/blob format');
          return { ...img, image_url: url };
        }
        console.warn('🎨 Invalid image URL format:', typeof url, url?.substring(0, 50));
        return { ...img, image_url: '/placeholder.svg', expired: true };
      }));

      console.log('🎨 Final processed images:', {
        count: processed.length,
        urlTypes: processed.map(img => ({
          type: img.image_url?.startsWith('data:') ? 'base64' : 
                img.image_url?.startsWith('blob:') ? 'blob' :
                img.image_url?.startsWith('/') ? 'placeholder' : 'unknown',
          length: img.image_url?.length
        }))
      });

      const newImages = [...state.generatedImages, ...processed];
      const newRemaining = data.remainingGenerations;
      const sessionToken = data.sessionToken || state.sessionToken;

      setState(prev => ({
        ...prev,
        sessionToken,
        remainingGenerations: newRemaining,
        generatedImages: newImages
      }));

      // Save to localStorage (this will handle storage quota gracefully)
      saveSession(sessionToken, newRemaining, newImages);

      toast.success(data.message || 'Image generated successfully!');
      
      if (newRemaining <= 0) {
        setTimeout(() => {
          toast.info('🎨 Sign up now to generate unlimited images and download full resolution!', {
            duration: 8000
          });
        }, 2000);
      }

      return data.images;
    } catch (error: any) {
      console.error('Guest generation error:', error);
      // Gracefully handle rate limit (429) by setting remaining to 0
      const msg: string = error?.message || '';
      const status: number | undefined = (error as any)?.context?.status || (error as any)?.status;
      if (status === 429 || /429|rate limit|limit exceeded/i.test(msg)) {
        setState(prev => ({ ...prev, remainingGenerations: 0 }));
        toast.error('Daily generation limit reached. Please sign up to continue.');
      } else {
        toast.error(msg || 'Failed to generate image');
      }
      return null;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const claimImages = async (userId: string) => {
    if (!state.sessionToken) return { count: 0, images: [] };

    try {
      // First, fetch the images before claiming using a SECURITY DEFINER RPC (bypasses RLS for guest rows)
      const { data: guestImages, error: fetchError } = await supabase
        .rpc('get_guest_images_by_token', {
          session_token: state.sessionToken,
        });

      if (fetchError) {
        console.error('Error fetching guest images via RPC:', fetchError);
      }

      // Now claim the images
      const { data, error } = await supabase.rpc('claim_guest_images', {
        session_token: state.sessionToken,
        user_id: userId
      });

      if (error) throw error;

      // Clear guest session after claiming
      localStorage.removeItem('guest_image_session');
      setState(prev => ({
        ...prev,
        sessionToken: null,
        remainingGenerations: 5,
        isLoading: false,
        generatedImages: []
      }));

      return { 
        count: data || 0, 
        images: guestImages || [] 
      };
    } catch (error: any) {
      console.error('Error claiming guest images:', error);
      toast.error('Failed to claim guest images');
      return { count: 0, images: [] };
    }
  };

  const resetGuestSession = () => {
    localStorage.removeItem('guest_image_session');
    setState(prev => ({
      ...prev,
      sessionToken: null,
      remainingGenerations: 5,
      isLoading: false,
      generatedImages: []
    }));
  };

  // Remove a single image by index (safe for items without id)
  const removeImageAt = (index: number) => {
    setState(prev => {
      const newImages = prev.generatedImages.filter((_, i) => i !== index);
      // Persist updated list
      saveSession(prev.sessionToken || '', prev.remainingGenerations, newImages);
      return { ...prev, generatedImages: newImages };
    });
  };

  // Clear all generated images but keep session and remaining count
  const clearImages = () => {
    setState(prev => {
      const newImages: any[] = [];
      saveSession(prev.sessionToken || '', prev.remainingGenerations, newImages);
      return { ...prev, generatedImages: [] };
    });
  };

  return {
    ...state,
    generateImage,
    claimImages,
    resetGuestSession,
    removeImageAt,
    clearImages,
    refreshRemaining,
    hasGuestImages: state.generatedImages.length > 0
  };
};